import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import type { AppUserRow } from '../database.types';

/**
 * The signed-in person's own profile row — M12 / S12-00.
 *
 * UI → THIS HOOK → `.select()` (ARCHITECTURE §2). **This hook writes nothing.**
 *
 * AUTHORIZATION IS RLS. `app_user_select_own` matches only the caller's own row
 * under forced row-level security, so another account's profile is unreachable
 * regardless of what is asked for.
 *
 * **IT EXISTS SO SETTINGS CAN SHOW THE SAVED TIMEZONE RATHER THAN THE DEVICE'S.**
 * That distinction is the whole point of the slice: `Intl` always has an answer
 * and it is frequently the wrong one. The canonical zone lives on this row
 * (D-009, INV-T17), and it is what every stored instant is rendered in.
 */
export type AppUserStatus = 'loading' | 'ready' | 'failed';

export interface AppUserState {
  status: AppUserStatus;
  /**
   * Null is an ORDINARY state, not an error: the profile is seeded on first
   * sign-in, and a read can win the race against it.
   */
  profile: AppUserRow | null;
  error: string | null;
  reload: () => Promise<void>;
}

export function useAppUser(): AppUserState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<AppUserStatus>(client ? 'loading' : 'failed');
  const [profile, setProfile] = useState<AppUserRow | null>(null);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  /**
   * The read can finish after the screen is gone — Brandon opens Settings and
   * immediately goes back. Setting state then is a React warning and, worse, a
   * write into a screen nobody is looking at. Same guard as the branding hook.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * **THE READ AND THE APPLY ARE SEPARATE**, matching `useCompanyBranding` and
   * `useCaptures`: fetching returns a result, and applying it is the only thing
   * that touches state.
   */
  const fetchProfile = useCallback(async (): Promise<
    { kind: 'ready'; row: AppUserRow | null } | { kind: 'failed' }
  > => {
    if (!client) return { kind: 'failed' };
    // **A THROW IS A FAILED READ, NOT AN UNHANDLED REJECTION.** This runs from
    // an effect through `void load()`, so anything that escapes here would
    // surface as a bare rejection with no screen state to show for it.
    try {
      const { data, error: queryError } = await client.from('app_user').select('*').maybeSingle();

      if (queryError) {
        reportError(queryError, 'settings.useAppUser');
        return { kind: 'failed' };
      }
      return { kind: 'ready', row: data ?? null };
    } catch (caught) {
      reportError(caught, 'settings.useAppUser');
      return { kind: 'failed' };
    }
  }, [client]);

  /**
   * Applying is the only thing that touches state, and a superseded read is
   * allowed to finish without writing — the same generation ticket
   * `useCompanyBranding` and `useCaptures` use, for the same reason.
   */
  const apply = useCallback(
    (result: { kind: 'ready'; row: AppUserRow | null } | { kind: 'failed' }) => {
      if (!alive.current) return;
      if (result.kind === 'failed') {
        setProfile(null);
        setError('Crewlink could not load your settings.');
        setStatus('failed');
        return;
      }
      setProfile(result.row);
      setError(null);
      setStatus('ready');
    },
    [],
  );

  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchProfile();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchProfile, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { status, profile, error, reload };
}
